/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { Component, Input, OnInit, ViewEncapsulation, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NotificationService } from '@alfresco/adf-core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { WatermarkConfig, WatermarkService } from './watermark.service';
import { WatermarkOverlayComponent } from './watermark-overlay.component';

/**
 * The watermark form an administrator sees inside Settings → administration.
 *
 * Everything is edited in a draft and only written when Save is pressed, the
 * same contract the branding panel follows. The mock page on the right renders
 * the real overlay component against the draft, so what is tried here is what
 * readers get — including this administrator's own name in the placeholders.
 */
@Component({
  selector: 'app-watermark-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatTooltipModule,
    WatermarkOverlayComponent,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './watermark-settings.component.html',
  styleUrl: './watermark-settings.component.scss'
})
export class WatermarkSettingsComponent implements OnInit {
  /** Whether the screen sits inside the settings page rather than on its own route. */
  @Input() embedded = false;

  private readonly watermarks = inject(WatermarkService);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);

  readonly draft = signal<WatermarkConfig>({ ...WatermarkService.DEFAULT });
  readonly busy = signal(false);

  /** Colours offered beside the free picker — muted ones read best under text. */
  readonly colourPresets = ['#475569', '#64748b', '#2563eb', '#0891b2', '#ca8a04', '#dc2626'];

  /** The fake document the preview pretends to be. */
  readonly sampleDoc = 'document.pdf';
  /** A fixed stamp for the preview so the sample does not tick every render. */
  readonly sampleTime = new Date();

  readonly limits = {
    opacityMin: WatermarkService.OPACITY_MIN,
    opacityMax: WatermarkService.OPACITY_MAX,
    fontMin: WatermarkService.FONT_MIN,
    fontMax: WatermarkService.FONT_MAX,
    logoScaleMin: WatermarkService.LOGO_SCALE_MIN,
    logoScaleMax: WatermarkService.LOGO_SCALE_MAX
  };

  /** Fake text lines behind the preview mark — pure decoration. */
  readonly previewLines = new Array(14);

  ngOnInit(): void {
    this.watermarks.load();
    this.draft.set({ ...this.watermarks.settings() });
  }

  /** Applies part of the form, keeping a fresh object so the preview notices. */
  patch(part: Partial<WatermarkConfig>): void {
    this.draft.update((current) => ({ ...current, ...part }));
  }

  /** Reads the picked image into the draft, as a data URI. */
  pickLogo(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      this.notification.showError(this.translate.instant('DMS.UI.A39AA9'));
      return;
    }
    if (file.size > WatermarkService.MAX_LOGO_BYTES) {
      this.notification.showError(this.translate.instant('DMS.UI.618305'));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => this.patch({ logo: String(reader.result ?? '') });
    reader.readAsDataURL(file);
  }

  removeLogo(): void {
    this.patch({ logo: '' });
  }

  /** The percentage shown beside the opacity slider. */
  get opacityLabel(): string {
    return `${Math.round(this.draft().opacity * 100)}%`;
  }

  save(): void {
    this.busy.set(true);
    this.watermarks
      .save(this.draft())
      .pipe(catchError(() => of(null)))
      .subscribe((result) => {
        this.busy.set(false);
        if (result === null) {
          this.notification.showError(this.translate.instant('DMS.WATERMARK.SAVE_FAILED'));
          return;
        }
        this.draft.set({ ...this.watermarks.settings() });
        this.notification.showInfo(this.translate.instant('DMS.WATERMARK.SAVED'));
      });
  }

  /** Everything back to the values a fresh deployment would use. */
  reset(): void {
    this.draft.set({ ...WatermarkService.DEFAULT });
  }
}
